import { z } from 'zod';
import { isoDateTime } from './common.js';
/**
 * Wire schemas mirroring the core domain. Every union is spelled out here
 * because a zod enum needs literals, and every one is asserted equal to its
 * domain type in tests/contract-types.test.ts — so a member added to core and
 * not to this file fails the type check rather than the runtime.
 */
export const recordKindSchema = z.enum([
    'message',
    'vehicle',
    'driver',
    'trip',
    'load',
    'alert',
    'document',
    'location',
    'hos',
]);
export const healthStateSchema = z.enum(['healthy', 'degraded', 'unhealthy', 'disabled']);
export const healthStatusSchema = z.object({
    state: healthStateSchema,
    detail: z.string().optional(),
    checkedAt: isoDateTime,
});
export const sourceSummarySchema = z.object({
    id: z.string(),
    enabled: z.boolean(),
    capabilities: z.array(z.string()),
    health: healthStatusSchema,
});
export const syncOutcomeSchema = z.object({
    sourceId: z.string(),
    pages: z.number().int(),
    records: z.number().int(),
    inserted: z.number().int(),
    updated: z.number().int(),
    unchanged: z.number().int(),
    durationMs: z.number(),
});
export const sourceRefSchema = z.object({
    sourceId: z.string(),
    externalId: z.string(),
    fetchedAt: isoDateTime,
});
/**
 * `raw` is never sent: it is the provider payload, kept for replay, and may
 * hold anything an adapter forgot to redact.
 */
export const recordSchema = z.object({
    id: z.string(),
    kind: recordKindSchema,
    source: sourceRefSchema,
    occurredAt: isoDateTime,
    title: z.string(),
    summary: z.string().optional(),
    data: z.unknown(),
    searchText: z.string().optional(),
});
export const questionOptionSchema = z.enum([
    'telegram',
    'email',
    'quickmanage',
    'samsara',
    'motive',
]);
export const questionOptionStatusSchema = z.object({
    option: questionOptionSchema,
    label: z.string(),
    available: z.boolean(),
});
export const replyLanguageSchema = z.enum(['uz-Latn', 'uz-Cyrl', 'ru', 'en']);
export const llmUsageSchema = z.object({
    calls: z.number().int(),
    inputTokens: z.number().int(),
    outputTokens: z.number().int(),
    cachedTokens: z.number().int(),
});
export const answerKindSchema = z.enum(['answered', 'unavailable', 'empty', 'disabled', 'failed']);
export const mailboxSchema = z.object({
    id: z.string(),
    email: z.email(),
    imapHost: z.string(),
    imapPort: z.number().int(),
    imapSecure: z.boolean(),
    folder: z.string(),
    enabled: z.boolean(),
    lastError: z.string().optional(),
});
export const sourceKindSchema = questionOptionSchema;
export const sourceFieldSchema = z.object({
    name: z.string(),
    label: z.string(),
    type: z.enum(['text', 'number', 'boolean', 'select', 'secret']),
    required: z.boolean(),
    secret: z.boolean(),
    default: z.union([z.string(), z.number(), z.boolean()]).optional(),
    options: z.array(z.string()).optional(),
    allowCustom: z.boolean().optional(),
    placeholder: z.string().optional(),
    help: z.string().optional(),
});
/** What the panel renders the "add a source" form from. */
export const sourceKindDescriptorSchema = z.object({
    kind: sourceKindSchema,
    label: z.string(),
    capabilities: z.array(z.string()),
    slugField: z.string(),
    fields: z.array(sourceFieldSchema),
    unavailable: z.string().optional(),
});
/**
 * A configured plugin. `secretsPresent` names the credential fields that are
 * set; the values themselves are never sent, which is why the form shows an
 * empty box and treats empty as "keep what is stored".
 */
export const sourceInstanceSchema = z.object({
    id: z.string(),
    kind: sourceKindSchema,
    sourceId: z.string(),
    label: z.string(),
    settings: z.record(z.string(), z.unknown()),
    secretsPresent: z.array(z.string()),
    enabled: z.boolean(),
    lastError: z.string().optional(),
    /** When its configuration last changed — "credentials updated" in the panel. */
    updatedAt: isoDateTime.optional(),
});
export const dayCountSchema = z.object({ day: z.string(), count: z.number().int() });
export const seriesPointSchema = dayCountSchema.extend({ key: z.string() });
export const sourceHealthSchema = z.object({
    sourceId: z.string(),
    records: z.number().int(),
    lastRecordAt: isoDateTime.optional(),
    lastRunAt: isoDateTime.optional(),
    lastRunError: z.string().optional(),
    lastRunRecords: z.number().int().optional(),
});
export const totalsSchema = z.object({
    records: z.number().int(),
    sources: z.number().int(),
    byKind: z.array(z.object({ kind: recordKindSchema, count: z.number().int() })),
});
export const questionStatsSchema = z.object({
    total: z.number().int(),
    answered: z.number().int(),
    calls: z.number().int(),
    inputTokens: z.number().int(),
    outputTokens: z.number().int(),
    cachedTokens: z.number().int(),
    byOption: z.array(z.object({ option: z.string(), count: z.number().int() })),
    perDay: z.array(dayCountSchema),
});
export { appUserSchema, userChannelSchema, userRoleSchema } from './users.js';
//# sourceMappingURL=domain.js.map